// Enums - Must match backend values exactly
export const TicketStatus = {
  Open: 1,
  InProgress: 2,
  Pending: 3,
  Resolved: 4,
  Closed: 5,
  OnHold: 6
} as const;

export type TicketStatus = typeof TicketStatus[keyof typeof TicketStatus];

export const Priority = {
  Low: 1,
  Medium: 2,
  High: 3,
  Critical: 4
} as const;

export type Priority = typeof Priority[keyof typeof Priority];

// Basic entity types
export interface UserSummary {
  id: string;
  fullName: string;
  email: string;
}

export interface CustomerSummary {
  id: string;
  fullName: string;
  email: string;
  company?: string;
  isArchived?: boolean;
}

export interface CategorySummary {
  id: string;
  name: string;
  description?: string;
  // "Billing › Refunds" - where the category sits in the tree
  path: string;
}

// Ticket categories form a tree up to this many levels deep (HierarchyTree.MaxCategoryDepth)
export const MAX_CATEGORY_DEPTH = 3;
export const CATEGORY_PATH_SEPARATOR = ' › ';

export interface CreateCategoryRequest {
  name: string;
  description?: string;
  // Omit for a top-level category
  parentCategoryId?: string;
  isVisibleInPortal?: boolean;
  suggestedCollectionId?: string;
}

// Clearable fields: omit to keep, send null to clear (move to the top level, remove the suggestion)
export interface UpdateCategoryRequest {
  name?: string;
  description?: string | null;
  parentCategoryId?: string | null;
  isVisibleInPortal?: boolean;
  suggestedCollectionId?: string | null;
}

// Every active child of the parent (the top level when null), in the new order
export interface ReorderCategoriesRequest {
  parentCategoryId: string | null;
  orderedIds: string[];
}

// Ticket types
// A ticket in the trash - restorable until purgeAt, when it's permanently deleted
export interface TrashedTicketResponse {
  id: string;
  ticketNumber: number;
  subject: string;
  status: TicketStatus;
  customerName: string;
  organizationId: string;
  organizationName: string;
  deletedAt: string;
  deletedByName?: string | null;
  purgeAt: string;
}

// Result of redacting text from a comment or description: how many times it appeared (each is now ▇ characters)
export interface RedactTextResponse {
  occurrences: number;
}

export interface TicketResponse {
  id: string;
  ticketNumber: number;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: Priority;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  closedAt?: string;
  dueAt?: string;
  isOverdue: boolean;
  assignedTo?: UserSummary;
  createdBy: UserSummary;
  customer: CustomerSummary;
  category?: CategorySummary;
  commentCount: number;
  attachmentCount: number;
  responseTime?: string;
  resolutionTime?: string;
  // Organization info for multi-org unified view
  organizationId: string;
  organizationName: string;
  organizationSubdomain: string;
  // Merge tracking - set when this ticket was merged into another
  mergedIntoTicketId?: string;
  mergedIntoTicketNumber?: number;
  // Customer portal only: false on a ticket the customer is a participant on - they can read and reply, nothing else
  isOwner?: boolean;
}

export interface TicketDetailResponse extends Omit<TicketResponse, 'commentCount' | 'attachmentCount'> {
  firstResponseAt?: string;
  comments: TicketCommentResponse[];
  attachments: TicketAttachmentResponse[];
  // SLA Time Tracking Metrics
  grossResolutionSeconds?: number;
  netResolutionSeconds?: number;
  openTimeSeconds: number;
  inProgressTimeSeconds: number;
  pendingTimeSeconds: number;
  onHoldTimeSeconds: number;
  firstResponseCalendarSeconds?: number;
  firstResponseBusinessSeconds?: number;
  grossResolutionBusinessSeconds?: number;
  netResolutionBusinessSeconds?: number;
  // Merge tracking
  mergedAt?: string;
  mergedFromTickets?: MergedTicketSummary[];
  // The organization requires a category before staff resolve a ticket
  categoryRequiredToResolve?: boolean;
  // Customer portal only: the owner may change the category (false when an agent filed it under an internal category)
  canChangeCategory?: boolean;
}

/** A ticket that was merged into this one. */
export interface MergedTicketSummary {
  id: string;
  ticketNumber: number;
  subject: string;
  mergedAt?: string;
}

export interface TicketCommentResponse {
  id: string;
  content: string;
  isInternal: boolean;
  isSystemGenerated: boolean;
  createdAt: string;
  updatedAt: string;
  authorName: string;
  authorEmail: string;
  isFromUser: boolean;
  attachments: TicketAttachmentResponse[];
}

export interface TicketAttachmentResponse {
  id: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  fileSizeFormatted: string;
  downloadUrl: string;
  uploadedAt: string;
  uploadedBy: string;
  fileHash: string;
  thumbnailUrl: string | null;
  previewUrl: string | null;
}

export interface TicketHistoryEntry {
  id: string;
  ticketId: string;
  action: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  description?: string;
  changedById?: string;
  createdAt: string;
  changedBy?: UserSummary;
  automationRuleId?: string;
  automationRuleName?: string;
}

export interface TicketListResponse {
  tickets: TicketResponse[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface TicketStatsResponse {
  totalTickets: number;
  openTickets: number;
  inProgressTickets: number;
  pendingTickets: number;
  resolvedTickets: number;
  overdueTickets: number;
  averageResponseTimeHours: number;
  averageResolutionTimeHours: number;
  lowPriorityTickets: number;
  mediumPriorityTickets: number;
  highPriorityTickets: number;
  criticalPriorityTickets: number;
}

export interface CustomerTicketStatsResponse {
  totalTickets: number;
  openTickets: number;
  inProgressTickets: number;
  pendingTickets: number;
  resolvedTickets: number;
  averageResolutionTimeDays: number;
  lowPriorityTickets: number;
  mediumPriorityTickets: number;
  highPriorityTickets: number;
  criticalPriorityTickets: number;
}

// Customer Portal Insights Types
export interface CustomerInsightsResponse {
  responseTimeTrend: ResponseTimeTrendPoint[];
  ticketVolumeTrend: TicketVolumeTrendPoint[];
  resolutionRateTrend: ResolutionRateTrendPoint[];
}

export interface ResponseTimeTrendPoint {
  label: string;
  month: number;
  year: number;
  averageResponseTimeHours: number;
}

export interface TicketVolumeTrendPoint {
  label: string;
  month: number;
  year: number;
  ticketsCreated: number;
  ticketsResolved: number;
}

export interface ResolutionRateTrendPoint {
  label: string;
  month: number;
  year: number;
  resolutionRatePercent: number;
}

// Customer Portal Recent Activity Types
export interface CustomerActivityResponse {
  id: string;
  ticketId: string;
  type: 'comment' | 'status_change' | 'assignment' | 'attachment' | 'update' | 'create';
  ticketNumber: number;
  ticketSubject: string;
  description: string;
  timestamp: string;
  status?: string;
}

// Request types
export interface CreateTicketRequest {
  subject: string;
  description: string;
  priority: Priority;
  categoryId?: string;
  customerId: string;
  assignedToId?: string;
  dueAt?: string; // ISO string date
  attachments?: File[]; // Files to upload with the ticket
  targetOrganizationId?: string; // For multi-org users creating tickets
  participantCustomerIds?: string[]; // Optional customers to add as participants when creating the ticket
}

export interface UpdateTicketRequest {
  subject?: string;
  description?: string;
  status?: TicketStatus;
  priority?: Priority;
  categoryId?: string | null;
  assignedToId?: string | null;
  dueAt?: string | null; // ISO string date
}

export interface AddCommentRequest {
  content: string;
  isInternal?: boolean;
  attachments?: File[]; // Files to upload with the comment
}

export interface MergeTicketRequest {
  targetTicketId: string;
  // Copy this ticket's customer and participants onto the target as participants (default on). They keep getting updates and can open
  // the target in the customer portal - leave off for an unrelated customer's ticket.
  copyCustomersToTarget?: boolean;
}

/** Summary of what a merge will move. `blockedReason` is null when the merge is allowed. */
export interface MergeTicketPreviewResponse {
  sourceTicketId: string;
  sourceTicketNumber: number;
  sourceSubject: string;
  targetTicketId: string;
  targetTicketNumber: number;
  targetSubject: string;
  commentsToMove: number;
  attachmentsToMove: number;
  participantsToMove: number;
  followersToMove: number;
  articleLinksToMove: number;
  addsSourceCustomerAsParticipant: boolean;
  blockedReason?: string | null;
}

export interface MergeTicketResponse {
  sourceTicketId: string;
  sourceTicketNumber: number;
  targetTicketId: string;
  targetTicketNumber: number;
  commentsMoved: number;
  attachmentsMoved: number;
  participantsMoved: number;
  followersMoved: number;
  articleLinksMoved: number;
  mergedAt: string;
}

// Customer portal ticket list: the customer's own requests, or the ones someone else opened and added them to as a participant
export type CustomerTicketScope = 'mine' | 'participating';

// What the customer viewing a portal ticket can do. A participant (isOwner false) can only comment.
export interface CustomerTicketActionsResponse {
  canResolve: boolean;
  canReopen: boolean;
  canComment: boolean;
  isOwner: boolean;
  currentStatus: number;
  availableStatuses: number[];
  isMerged: boolean;
  // The ticket this one was merged into - only when the customer can open it there
  mergedIntoTicketId?: string | null;
  mergedIntoTicketNumber?: number | null;
}

export interface TicketFilterRequest {
  organizationIds?: string[];  // For multi-org filtering in unified view
  statuses?: TicketStatus[];
  priorities?: Priority[];
  assignedToIds?: string[];
  showUnassigned?: boolean;
  showOverdue?: boolean;
  followedByMe?: boolean;  // "Following": tickets the signed-in user follows (the server takes the user from the token)
  categoryIds?: string[];
  customerIds?: string[];
  searchQuery?: string;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDescending?: boolean;
}

// Utility types
export const TicketStatusLabels: Record<TicketStatus, string> = {
  [TicketStatus.Open]: 'Open',
  [TicketStatus.InProgress]: 'In Progress',
  [TicketStatus.Pending]: 'Pending',
  [TicketStatus.Resolved]: 'Resolved',
  [TicketStatus.Closed]: 'Closed',
  [TicketStatus.OnHold]: 'On Hold'
};

export const PriorityLabels: Record<Priority, string> = {
  [Priority.Low]: 'Low',
  [Priority.Medium]: 'Medium',
  [Priority.High]: 'High',
  [Priority.Critical]: 'Critical'
};

export const TicketStatusColors: Record<TicketStatus, string> = {
  [TicketStatus.Open]: 'bg-blue-100 text-blue-800',
  [TicketStatus.InProgress]: 'bg-yellow-100 text-yellow-800',
  [TicketStatus.Pending]: 'bg-orange-100 text-orange-800',
  [TicketStatus.Resolved]: 'bg-green-100 text-green-800',
  [TicketStatus.Closed]: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  [TicketStatus.OnHold]: 'bg-purple-100 text-purple-800'
};

export const PriorityColors: Record<Priority, string> = {
  [Priority.Low]: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  [Priority.Medium]: 'bg-blue-100 text-blue-800',
  [Priority.High]: 'bg-orange-100 text-orange-800',
  [Priority.Critical]: 'bg-red-100 text-red-800'
};

// Backend-compatible response types
export interface CategoryDetailResponse {
  id: string;
  name: string;
  description?: string | null;
  parentCategoryId?: string | null;
  // "Billing › Refunds"
  path: string;
  // 1 for a top-level category
  depth: number;
  sortOrder: number;
  isVisibleInPortal: boolean;
  // False when this category or one above it is hidden from customers
  isEffectivelyVisibleInPortal: boolean;
  suggestedCollectionId?: string | null;
  suggestedCollectionName?: string | null;
  childCount: number;
  createdAt: string;
  updatedAt: string;
  ticketCount: number;
  openTicketCount: number;
  // Set when the category is archived (left out of pickers, kept on existing tickets)
  archivedAt?: string | null;
}

// The whole tree in display order: each category followed by its subcategories
export interface CategoryListResponse {
  categories: CategoryDetailResponse[];
  totalCount: number;
}

export interface UserDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  initials: string;
  avatar?: string;
  timeZone?: string;
  language?: string;
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
  organizations: UserOrganizationDto[];
  roles?: string[];
}

export interface UserOrganizationDto {
  organizationId: string;
  organizationName: string;
  role: string;
  isActive: boolean;
  isAssignable: boolean;
  joinedAt: string;
}

export interface UserListResponse {
  users: UserDto[];
  totalCount: number;
  pageCount: number;
  currentPage: number;
  pageSize: number;
}

// Lookup types
export interface StatusLookupResponse {
  value: number;
  name: string;
  label: string;
}

export interface PriorityLookupResponse {
  value: number;
  name: string;
  label: string;
}

export interface ArticleStatusLookupResponse {
  value: number;
  name: string;
  label: string;
}

export interface LookupsResponse {
  statuses: StatusLookupResponse[];
  priorities: PriorityLookupResponse[];
  articleStatuses: ArticleStatusLookupResponse[];
}

// A category a customer may pick on the portal (active, not internal), in tree order
export interface PortalCategoryResponse {
  id: string;
  name: string;
  path: string;
  description?: string | null;
  parentCategoryId?: string | null;
  depth: number;
}

// "These articles might help" on the portal's new-ticket form
export interface PortalSuggestedArticle {
  id: string;
  title: string;
  summary?: string | null;
  collectionPath?: string | null;
  // The article's portal address (its id works too)
  slug?: string | null;
}

export interface CustomerPortalLookupsResponse {
  priorities: PriorityLookupResponse[];
  statuses: StatusLookupResponse[];
}

// Dashboard and reports types
export interface ReportDataPoint {
  label: string;
  value: number;
  date?: string;
  metadata?: Record<string, unknown>;
}

export interface TicketTrendDataPoint {
  label: string;
  date: string;
  ticketsCreated: number;
  ticketsCompleted: number;
}

export interface DashboardActivityDto {
  id: string;
  type: string;
  action: string;
  description: string;
  timestamp: string;
  userName?: string;
  ticketId?: string;
  ticketNumber?: number;
  ticketSubject?: string;
  organizationId?: string;
  organizationName?: string;
}

export interface DashboardStatsDto {
  totalTickets: number;
  openTickets: number;
  inProgressTickets: number;
  pendingTickets: number;
  onHoldTickets: number;
  resolvedTickets: number;
  overdueTickets: number;
  averageResponseTimeHours: number;
  averageResolutionTimeHours: number;
  lowPriorityTickets: number;
  mediumPriorityTickets: number;
  highPriorityTickets: number;
  criticalPriorityTickets: number;
  // SLA Time Tracking Metrics
  averageNetResolutionTimeHours?: number;
  averageFirstResponseCalendarHours?: number;
  averageFirstResponseBusinessHours?: number;
  averageGrossResolutionBusinessHours?: number;
  averageNetResolutionBusinessHours?: number;
  slaTrackedTicketCount: number;
  // Last 30 Days Smart Metrics (uses business hours if configured, otherwise calendar)
  averageFirstResponseHoursLast30Days?: number;
  averageResolutionHoursLast30Days?: number;
}

// Tickets by category (GET /reports/categories): tickets created in a range, per category and rolled up with
// its subcategories
export interface CategoryMetrics {
  created: number;
  // Not yet Resolved or Closed
  open: number;
  // Resolved or Closed, merged duplicates excluded
  resolved: number;
  avgFirstResponseHours: number | null;
  avgResolutionHours: number | null;
  firstResponseSampleSize: number;
  resolutionSampleSize: number;
}

export interface CategoryReportRow {
  categoryId: string;
  parentCategoryId: string | null;
  name: string;
  path: string;
  depth: number;
  isArchived: boolean;
  own: CategoryMetrics;
  rollup: CategoryMetrics;
}

export interface CategoryReportResponse {
  fromDate: string;
  toDate: string;
  totals: CategoryMetrics;
  uncategorized: CategoryMetrics;
  // Tree order, only categories with tickets in the range
  categories: CategoryReportRow[];
}

export interface DashboardOverviewResponse {
  ticketTrends: TicketTrendDataPoint[];
  recentActivities: DashboardActivityDto[];
  stats: DashboardStatsDto;
}

// Ticket Participants (CCs) - visible to everyone
export interface ParticipantResponse {
  id: string;
  customerId: string;
  fullName: string;
  email: string;
  company?: string;
  addedAt: string;
  addedBy?: string;
}

export interface AddParticipantRequest {
  customerId: string;
}

export interface AddParticipantsRequest {
  customerIds: string[];
}

export interface EligibleParticipantResponse {
  id: string;
  fullName: string;
  email: string;
  company?: string;
}

// Ticket Followers (Watchers) - internal staff only
export interface FollowerResponse {
  id: string;
  userId: string;
  fullName: string;
  email: string;
  followedAt: string;
}

export interface TicketFollowersResponse {
  ticketId: string;
  followers: FollowerResponse[];
  isCurrentUserFollowing: boolean;
  followerCount: number;
}

export interface FollowTicketResponse {
  success: boolean;
  isFollowing: boolean;
  message: string;
}